import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  styled,
  CircularProgress,
  Alert,
  Grow,
  Slide
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  Restaurant as RestaurantIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  FiberNew as FiberNewIcon
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import firebaseDataService from '../../services/FirebaseDataService';
import * as signalR from '@microsoft/signalr';
import { infiniteRetryPolicy, startResilientConnection } from '../../utils/signalRConnection';
import { mainApi } from '../../config/apiClient';
import { getAccessToken } from '../../services/auth/ownAuthSession';

//  RENK PALETİ (Classy Siyah Tema)
const PRIMARY_COLOR_CLASSY = '#1A75BB';
const READY_COLOR_CLASSY = '#008060';
const BACKGROUND_COLOR_BLACK = '#000000';
const CARD_BACKGROUND_DEEP = '#101010';
const TEXT_COLOR_BRIGHT = '#F0F0F0';

// Styled Components
const DisplayContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  backgroundColor: BACKGROUND_COLOR_BLACK,
  overflow: 'hidden',
  fontFamily: 'Roboto, Arial, sans-serif',
}));

const DisplayHeader = styled(Box)(({ theme }) => ({
  backgroundColor: '#0A0A0A',
  padding: theme.spacing(2, 3),
  textAlign: 'center',
  boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.8)',
  borderBottom: '1px solid #333',
}));

const ContentArea = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(3),
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#101010',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#444',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#666',
  },
}));
interface OrderCardProps {
  status: 'ready' | 'preparing';
}

const OrderCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'status',
})<OrderCardProps>(({ theme, status }) => ({
  height: '120px',
  borderRadius: theme.spacing(1.5),
  transition: 'transform 0.3s, box-shadow 0.3s',
  border: status === 'ready' ? `3px solid ${READY_COLOR_CLASSY}` : `3px solid ${PRIMARY_COLOR_CLASSY}`,
  backgroundColor: CARD_BACKGROUND_DEEP,
  color: TEXT_COLOR_BRIGHT,
  boxShadow: `0 4px 15px ${status === 'ready' ? `${READY_COLOR_CLASSY}30` : `${PRIMARY_COLOR_CLASSY}30`}`,
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 6px 20px ${status === 'ready' ? `${READY_COLOR_CLASSY}50` : `${PRIMARY_COLOR_CLASSY}50`}`,
  },
}));

//  BİLDİRİM KOMPONENTİ 
interface Notification {
  type: 'NEW' | 'READY';
  orderId?: string;
}
const NotificationOverlay: React.FC<{ notification: Notification | null, onClose: () => void }> = ({ notification, onClose }) => {
  if (!notification) return null;

  const isNew = notification.type === 'NEW';
  const bgColor = isNew ? PRIMARY_COLOR_CLASSY : READY_COLOR_CLASSY;
  const mainText = isNew ? 'YENİ SİPARİŞ' : 'SİPARİŞ HAZIRLANDI';
  const icon = isNew ? <FiberNewIcon sx={{ fontSize: 100 }} /> : <CheckCircleOutlineIcon sx={{ fontSize: 100 }} />;

  return (
    <Slide direction="up" in={!!notification} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 9999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: `${bgColor}e6`,
          color: 'white',
          textAlign: 'center',
          cursor: 'pointer',
        }}
        onClick={onClose}
      >
        <Box sx={{ animation: isNew ? 'pulse 1.5s infinite' : 'none' }}>
          <style>{`
            @keyframes pulse {
              0% { transform: scale(1); }
              50% { transform: scale(1.05); }
              100% { transform: scale(1); }
            }
          `}</style>
          {icon}
          <Typography variant="h1" sx={{ fontWeight: 'bold', fontSize: '8rem', mt: 2 }}>
            {mainText}
          </Typography>
          {notification.orderId && (
            <Typography variant="h2" sx={{ fontSize: '5rem', mt: 1 }}>
              {/* === DÜZELTME ===: Gereksiz asteriksler (**) kaldırıldı. */}
              Kodu: {notification.orderId}
            </Typography>
          )}
        </Box>
      </Box>
    </Slide>
  );
};
interface Order {
  id: number;
  siparisId: string;
  masaNumarasi: number;
  siparisDurumu: string;
  siparisOlusturmaTarihi: string;
  qrMenuId?: string;
}

const PoleDisplayScreen: React.FC = () => {
  const { menuId } = useParams<{ menuId: string }>();
  const { currentUser } = useAuth();
  
  // State
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [menuName, setMenuName] = useState<string>('');
  const [notification, setNotification] = useState<Notification | null>(null);
  // SignalR connection
  const [connectionStatus, setConnectionStatus] = useState<string>('Disconnected');
  
  // SignalR'dan gelen siparişleri takip etmek için (duplicate önleme)
  const processedSignalROrders = useRef<Set<string>>(new Set());
  // === YENİ FONKSİYON ===
  // (SignalR useEffect'inden ÖNCE tanımlanması, orada kullanılabilmesi için önemlidir)
  const shortenOrderId = (siparisId: string): string => {
    if (!siparisId) {
      return '';
    }
    return siparisId;
  };
  // Notification değiştiğinde 2 saniye timer başlat
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Zaman güncelleyici
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 10000); // Her 10 saniyede güncelle

    return () => clearInterval(timer);
  }, []);

  // Şirket ID'sini al
  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser) {
        try {
          const data = await firebaseDataService.getUserData(currentUser.uid);
          if (data) {
            setCompanyId(data.companyId || '');
          }
        } catch (error) {
          console.error('❌ Kullanıcı bilgisi alma hatası:', error);
          setError('Kullanıcı bilgileri alınamadı.');
        }
      }
    };
    fetchUserData();
  }, [currentUser]);

  // Menü adını al
  useEffect(() => {
    const fetchMenuName = async () => {
      if (!companyId || !menuId) return;

      try {
        const timestamp = new Date().getTime();
        const response = await mainApi.get(`/api/QRMenu/menu-definitions/${menuId}`, {
          params: { _t: timestamp },
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        });
        
        const result = response.data;
        if (result.success && result.data) {
          setMenuName(result.data.name || 'Menü');
        }
      } catch (error) {
        console.error('❌ Menü adı yükleme hatası:', error);
      }
    };

    fetchMenuName();
  }, [companyId, menuId]);

  // SignalR bağlantısını kur
  useEffect(() => {
    if (!companyId) return;

    let stopConnection: (() => void) | null = null;

    const setupSignalRConnection = async () => {
      try {
        const apiUrl = process.env.REACT_APP_NETAPI_URL;
        const connection = new signalR.HubConnectionBuilder()
          .withUrl(`${apiUrl}/ordershub`, {
            accessTokenFactory: async () => (await getAccessToken().catch(() => null)) ?? '',
            skipNegotiation: true,
            transport: signalR.HttpTransportType.WebSockets,
          })
          .withAutomaticReconnect(infiniteRetryPolicy)
          .configureLogging(signalR.LogLevel.None)
          .build();

        connection.on('NewOrder', (orderData) => {
          console.log('🔔 SignalR: Yeni sipariş geldi:', orderData.siparisId);
          if (orderData.qrMenuId === menuId) {
            // Duplicate kontrol
            const orderKey = `${orderData.id}_${orderData.siparisId}`;
            if (processedSignalROrders.current.has(orderKey)) {
              console.log('⏭️ SignalR: DUPLICATE - Sipariş zaten işlendi:', orderData.siparisId);
              return;
            }
            
            processedSignalROrders.current.add(orderKey);
            // === DÜZELTME ===: Sipariş ID'si kısaltılarak bildirime gönderildi.
            setNotification({ type: 'NEW', orderId: shortenOrderId(orderData.siparisId) });
            
            setTimeout(() => {
              processedSignalROrders.current.delete(orderKey);
            }, 30000);
            
            const newOrder: Order = {
              id: orderData.id,
              siparisId: orderData.siparisId,
              masaNumarasi: orderData.masaNumarasi || orderData.masaId,
              siparisDurumu: orderData.siparisDurumu,
              siparisOlusturmaTarihi: orderData.siparisOlusturmaTarihi,
              qrMenuId: orderData.qrMenuId
            };
            
            setOrders(prevOrders => {
              const existingOrderIndex = prevOrders.findIndex(order => order.id === newOrder.id);
              
              if (existingOrderIndex !== -1) {
                const updatedOrders = [...prevOrders];
                updatedOrders[existingOrderIndex] = newOrder;
                return updatedOrders.sort((a, b) => 
                  new Date(a.siparisOlusturmaTarihi).getTime() - new Date(b.siparisOlusturmaTarihi).getTime()
                );
              } else {
                console.log(`➕ SignalR: Yeni sipariş listeye eklendi: ${newOrder.siparisId}`);
                return [newOrder, ...prevOrders].sort((a, b) => 
                  new Date(a.siparisOlusturmaTarihi).getTime() - new Date(b.siparisOlusturmaTarihi).getTime()
                );
              }
            });
          }
        });

        // === TAMAMEN YENİLENMİŞ BLOK (Stale State sorunu çözüldü) ===
        connection.on('OrderStatusUpdated', (orderData) => {
          console.log('🔄 SignalR: Sipariş durumu güncelleme:', orderData.siparisId, '->', orderData.siparisDurumu);

          setOrders(prevOrders => {
            // 1. Siparişin listede olup olmadığını kontrol et
            const orderIndex = prevOrders.findIndex(order => order.id === orderData.id || order.siparisId === orderData.siparisId);
            const isOrderInCurrentList = orderIndex !== -1;

            // 2. Bu güncellemenin bu ekranla ilgili olup olmadığını kontrol et
            const isRelevant = orderData.qrMenuId === menuId || isOrderInCurrentList;

            if (!isRelevant) {
              // Bizimle ilgili değilse, state'i değiştirme
              return prevOrders;
            }

            // 3. BİLDİRİM MANTIĞI:
            // Sadece yeni durum 'Hazır' ise VE önceki durumu 'Hazır' değilse bildirim gönder
            const isNewStatusReady = orderData.siparisDurumu === 'Hazır';
            let oldStatus = '';
            if (isOrderInCurrentList) {
              oldStatus = prevOrders[orderIndex].siparisDurumu;
            }

            if (isNewStatusReady && oldStatus !== 'Hazır') {
              console.log('✅ Sipariş "Hazır" durumuna geçti. Bildirim tetikleniyor!');
              // === DÜZELTME ===: Sipariş ID'si kısaltılarak bildirime gönderildi.
              setNotification({ type: 'READY', orderId: shortenOrderId(orderData.siparisId) });
            }
            
            // 4. State Güncelleme
            const updatedOrders = prevOrders.map(order => 
              (order.id === orderData.id || order.siparisId === orderData.siparisId)
                ? { ...order, siparisDurumu: orderData.siparisDurumu }
                : order
            );
            
            // 5. Filtreleme (İptal ve Teslim Edildi hariç)
            const filteredOrders = updatedOrders.filter(order => 
              order.siparisDurumu === 'Beklemede' || 
              order.siparisDurumu === 'Hazırlanıyor' || 
              order.siparisDurumu === 'Hazır'
            );
            
            // 6. Sıralamayı koru
            filteredOrders.sort((a, b) => 
              new Date(a.siparisOlusturmaTarihi).getTime() - new Date(b.siparisOlusturmaTarihi).getTime()
            );

            return filteredOrders;
          });
        });
        // ========================================================

        connection.on('OrderDeleted', (orderData) => {
          if (orderData.qrMenuId === menuId) {
            setOrders(prevOrders => prevOrders.filter(order => order.id !== orderData.id));
          }
        });

        // Gruba katılma onConnected içinde: yeniden bağlanma yeni bir connection id
        // ürettiği için sunucudaki grup üyeliği kaybolur.
        stopConnection = startResilientConnection(connection, {
          label: 'PoleDisplay',
          onConnected: () => connection.invoke('JoinCompanyGroup', companyId),
          onStatusChange: setConnectionStatus,
        });
      } catch (error) {
        setConnectionStatus('Error');
        console.error('❌ SignalR bağlantı hatası:', error);
      }
    };

    setupSignalRConnection();

    return () => {
      stopConnection?.();
    };
  }, [companyId, menuId]); // shortenOrderId'yi bağımlılığa eklemeye gerek yok.

  // Siparişleri yükle
  const fetchOrders = async () => {
    if (!companyId) return;

    try {
      const timestamp = new Date().getTime();
      const response = await mainApi.get(`/api/MasaSiparisi/orders`, {
        params: { _t: timestamp },
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
      });
      
      const result = response.data;
      if (result.success && result.data) {
        const allOrders: Order[] = result.data.map((order: any) => ({
          id: order.id,
          siparisId: order.siparisId,
          masaNumarasi: order.masaNumarasi || order.masaId,
          siparisDurumu: order.siparisDurumu,
          siparisOlusturmaTarihi: order.siparisOlusturmaTarihi,
          qrMenuId: order.qrMenuId
        }));

        const displayOrders = allOrders.filter(order => 
          order.qrMenuId === menuId && 
          (order.siparisDurumu === 'Beklemede' || 
           order.siparisDurumu === 'Hazırlanıyor' || 
           order.siparisDurumu === 'Hazır')
        );

        displayOrders.sort((a, b) => 
          new Date(a.siparisOlusturmaTarihi).getTime() - new Date(b.siparisOlusturmaTarihi).getTime()
        );

        setOrders(displayOrders);
      }
    } catch (error: any) {
      if (error.response?.status === 404) {
        setOrders([]);
      } else {
        setError('Siparişler yüklenirken hata oluştu.');
      }
    } finally {
      setLoading(false);
    }
  };

  // İlk yükleme
  useEffect(() => {
    if (companyId && menuId) {
      fetchOrders();
    }
  }, [companyId, menuId]);

  // Geçen süreyi hesapla
  const calculateElapsedMinutes = (orderDate: string): number => {
    const orderTime = new Date(orderDate);
    const now = new Date();
    const diffMs = now.getTime() - orderTime.getTime();
    return Math.floor(diffMs / (1000 * 60));
  };

  // renderOrderCard FONKSİYONU TANIMI BAŞLANGICI
  const renderOrderCard = (order: Order) => {
    const elapsedMinutes = calculateElapsedMinutes(order.siparisOlusturmaTarihi);
    const isReady = order.siparisDurumu === 'Hazır';

    return (
      <Grid item xs={6} sm={4} md={3} key={order.id}>
        <Grow in={true} timeout={500}>
          {/* OrderCard burada kullanıldı */}
          <OrderCard status={isReady ? 'ready' : 'preparing'}> 
            <CardContent sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'center',
              height: '100%',
              p: 1,
              '&:last-child': { pb: 1 }
            }}>
              {/* === DÜZELTME ===: Sipariş ID'si kısaltıldı. */}
              <Typography variant="h3" sx={{
                fontWeight: '900',
                color: isReady ? READY_COLOR_CLASSY : PRIMARY_COLOR_CLASSY, 
                lineHeight: 1.1,
                fontSize: '2.5rem'
              }}>
                {shortenOrderId(order.siparisId)}
              </Typography>

              {/* Geçen süre bilgisi */}
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <AccessTimeIcon sx={{
                  fontSize: 18,
                  mr: 0.5,
                  color: isReady ? READY_COLOR_CLASSY : PRIMARY_COLOR_CLASSY
                }} />
                <Typography variant="body1" sx={{
                  color: TEXT_COLOR_BRIGHT,
                  fontWeight: 'bold',
                  fontSize: '1.1rem'
                }}>
                  {elapsedMinutes} Dk
                </Typography>
              </Box>
            </CardContent>
          </OrderCard>
        </Grow>
      </Grid>
    );
  };

  // Sipariş kaynağını belirle
  const getOrderSource = (order: Order): string => {
    if (order.masaNumarasi && order.masaNumarasi > 0) {
      return `Masa ${order.masaNumarasi}`;
    }
    
    if (order.siparisId.startsWith('CC')) {
      return 'Çağrı Merkezi';
    } else if (order.siparisId.startsWith('EC')) {
      return 'E-ticaret';
    } else if (order.siparisId.startsWith('P-')) {
      return 'Paket';
    } else if (order.siparisId.startsWith('D-')) {
      return 'Kiosk';
    }
    
    return 'Sipariş';
  };

  // shortenOrderId fonksiyonu buradan alındı ve SignalR useEffect'inden ÖNCEYE taşındı.

  if (loading) {
    return (
      <DisplayContainer>
        <DisplayHeader>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                label="Yükleniyor..."
                sx={{
                  bgcolor: 'white',
                  color: BACKGROUND_COLOR_BLACK,
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  padding: '8px 16px'
                }}
              />
              <CircularProgress size={12} sx={{ color: PRIMARY_COLOR_CLASSY }} />
            </Box>

            <Typography variant="body2" sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontWeight: 500
            }}>
              {menuName}
            </Typography>
          </Box>
        </DisplayHeader>
        <ContentArea>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <CircularProgress size={80} sx={{ color: PRIMARY_COLOR_CLASSY }} />
          </Box>
        </ContentArea>
      </DisplayContainer>
    );
  }

  if (error) {
    return (
      <DisplayContainer>
        <DisplayHeader>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                label="Hata"
                sx={{
                  bgcolor: '#f44336',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  padding: '8px 16px'
                }}
              />
              <Box sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: '#f44336',
                boxShadow: '0 0 8px rgba(244, 67, 54, 0.8)'
              }} />
            </Box>

            <Typography variant="body2" sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontWeight: 500
            }}>
              {menuName}
            </Typography>
          </Box>
        </DisplayHeader>
        <ContentArea>
          <Alert severity="error" sx={{ mb: 2, bgcolor: '#101010', color: TEXT_COLOR_BRIGHT }}>
            {error}
          </Alert>
        </ContentArea>
      </DisplayContainer>
    );
  }

  return (
    <DisplayContainer>
      {/* 💥 BİLDİRİM OVERLAY 💥 */}
      <NotificationOverlay notification={notification} onClose={() => setNotification(null)} />

      <DisplayHeader>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={`${orders.length} AKTİF SİPARİŞ`}
              sx={{
                bgcolor: 'white',
                color: BACKGROUND_COLOR_BLACK,
                fontWeight: 'bold',
                fontSize: '1rem',
                padding: '8px 16px'
              }}
            />
            <Box sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: connectionStatus === 'Connected' ? READY_COLOR_CLASSY : '#f44336',
              boxShadow: connectionStatus === 'Connected'
                ? `0 0 8px ${READY_COLOR_CLASSY}`
                : '0 0 8px rgba(244, 67, 54, 0.8)'
            }} />
          </Box>

          <Typography variant="h6" sx={{
            color: TEXT_COLOR_BRIGHT,
            fontWeight: 'normal'
          }}>
            {menuName}
          </Typography>
        </Box>
      </DisplayHeader>

      <ContentArea>
        {/* Boş Durum */}
        {orders.length === 0 ? (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '70vh',
            textAlign: 'center'
          }}>
            <RestaurantIcon sx={{ fontSize: 80, color: '#444', mb: 2 }} />
            <Typography variant="h4" sx={{ color: TEXT_COLOR_BRIGHT, mb: 1 }}>
              Aktif Sipariş Yok
            </Typography>
            <Typography variant="body1" sx={{ color: '#aaa' }}>
              Yeni siparişler burada görünecektir. Bağlantı durumu: {connectionStatus}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={4} sx={{ height: '100%' }}>
            {/* HAZIRLANIYOR Bölümü (Beklemede + Hazırlanıyor) */}
            <Grid item xs={12} md={6}>
              <Box sx={{
                bgcolor: CARD_BACKGROUND_DEEP,
                borderRadius: 2,
                p: 2,
                height: '100%',
                boxShadow: `0 0 10px rgba(0, 0, 0, 0.6)`
              }}>
                <Typography variant="h4" sx={{
                  color: PRIMARY_COLOR_CLASSY,
                  textAlign: 'center',
                  mb: 3,
                  fontWeight: 'bold',
                  pb: 1,
                  borderBottom: `3px solid ${PRIMARY_COLOR_CLASSY}`
                }}>
                  HAZIRLANIYOR
                </Typography>

                <Grid container spacing={2}>
                  {orders
                    .filter(order => order.siparisDurumu === 'Beklemede' || order.siparisDurumu === 'Hazırlanıyor')
                    .map(renderOrderCard)}
                </Grid>

                {orders.filter(order => order.siparisDurumu === 'Beklemede' || order.siparisDurumu === 'Hazırlanıyor').length === 0 && (
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 'calc(100% - 90px)',
                    textAlign: 'center'
                  }}>
                    <RestaurantIcon sx={{ fontSize: 60, color: '#444', mb: 1 }} />
                    <Typography variant="h6" sx={{ color: '#aaa' }}>
                      Şu an hazırlanan sipariş yok
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* HAZIR Bölümü */}
            <Grid item xs={12} md={6}>
              <Box sx={{
                bgcolor: CARD_BACKGROUND_DEEP,
                borderRadius: 2,
                p: 2,
                height: '100%',
                boxShadow: `0 0 10px rgba(0, 0, 0, 0.6)`
              }}>
                <Typography variant="h4" sx={{
                  color: READY_COLOR_CLASSY,
                  textAlign: 'center',
                  mb: 3,
                  fontWeight: 'bold',
                  pb: 1,
                  borderBottom: `3px solid ${READY_COLOR_CLASSY}`
                }}>
                  HAZIR
                </Typography>
                
                <Grid container spacing={1.5}>
                  {orders
                    .filter(order => order.siparisDurumu === 'Hazır')
                    .map((order) => {
                      const elapsedMinutes = calculateElapsedMinutes(order.siparisOlusturmaTarihi);
                      return (
                        <Grid item xs={6} sm={4} md={3} key={order.id}>
                          <Card sx={{
                            bgcolor: '#e8f5e8',
                            border: '2px solid #4caf50',
                            borderRadius: 1,
                            height: '100px'
                          }}>
                            <CardContent sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              alignItems: 'center',
                              height: '100%',
                              p: 1,
                              '&:last-child': { pb: 1 }
                            }}>
                              {/* === DÜZELTME ===: Sipariş ID'si kısaltıldı. */}
                              <Typography variant="h4" sx={{ 
                                fontWeight: 'bold', 
                                color: '#333',
                                lineHeight: 1
                              }}>
                                #{shortenOrderId(order.siparisId)}
                              </Typography>
                              
                              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                <AccessTimeIcon sx={{ fontSize: 16, mr: 0.3, color: '#2e7d32' }} />
                                <Typography variant="body2" sx={{ color: '#666', fontWeight: 'bold' }}>
                                  {elapsedMinutes}dk
                                </Typography>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                </Grid>

                {orders.filter(order => order.siparisDurumu === 'Hazır').length === 0 && (
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 'calc(100% - 90px)',
                    textAlign: 'center'
                  }}>
                    <RestaurantIcon sx={{ fontSize: 60, color: '#444', mb: 1 }} />
                    <Typography variant="h6" sx={{ color: '#aaa' }}>
                      Teslim edilecek hazır sipariş yok
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        )}
      </ContentArea>
    </DisplayContainer>
  );
};

export default PoleDisplayScreen;